import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, AlertTriangle, Gem, Star, Crown } from 'lucide-react';
import { serviceAPI } from '../api/serviceAPI';
import { categoryLabel } from '../i18n/categoryNames';
import './ServiceCreatePage.css';

interface ServicePackage {
  price: number;
  delivery_days: number;
  revisions: number;
  features: string[];
}

interface FAQ {
  question: string;
  answer: string;
}

const ServiceCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 기본 정보
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');

  // 패키지 정보 (3가지)
  const [basicPackage, setBasicPackage] = useState<ServicePackage>({
    price: 50000,
    delivery_days: 3,
    revisions: 1,
    features: [],
  });

  const [standardPackage, setStandardPackage] = useState<ServicePackage>({
    price: 100000,
    delivery_days: 5,
    revisions: 2,
    features: [],
  });

  const [premiumPackage, setPremiumPackage] = useState<ServicePackage>({
    price: 200000,
    delivery_days: 7,
    revisions: 3,
    features: [],
  });

  // 추가 정보
  const [portfolioImages, setPortfolioImages] = useState<string[]>([]);
  const [portfolioInput, setPortfolioInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [faqs, setFaqs] = useState<FAQ[]>([{ question: '', answer: '' }]);
  const [requirements, setRequirements] = useState('');
  
  // 추가 옵션
  const [sourceFilesIncluded, setSourceFilesIncluded] = useState(false);
  const [commercialUse, setCommercialUse] = useState(false);

  // 기능 입력
  const [basicFeatureInput, setBasicFeatureInput] = useState('');
  const [standardFeatureInput, setStandardFeatureInput] = useState('');
  const [premiumFeatureInput, setPremiumFeatureInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  const categories = serviceAPI.getCategories();

  // 태그 관리
  const addTag = () => {
    if (tagInput.trim() && tags.length < 10) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  // 포트폴리오 이미지 관리
  const addPortfolioImage = () => {
    if (portfolioInput.trim()) {
      setPortfolioImages([...portfolioImages, portfolioInput.trim()]);
      setPortfolioInput('');
    }
  };

  const removePortfolioImage = (index: number) => {
    setPortfolioImages(portfolioImages.filter((_, i) => i !== index));
  };

  // 기능 관리
  const addFeature = (packageType: 'basic' | 'standard' | 'premium') => {
    if (packageType === 'basic' && basicFeatureInput.trim()) {
      setBasicPackage({
        ...basicPackage,
        features: [...basicPackage.features, basicFeatureInput.trim()]
      });
      setBasicFeatureInput('');
    } else if (packageType === 'standard' && standardFeatureInput.trim()) {
      setStandardPackage({
        ...standardPackage,
        features: [...standardPackage.features, standardFeatureInput.trim()]
      });
      setStandardFeatureInput('');
    } else if (packageType === 'premium' && premiumFeatureInput.trim()) {
      setPremiumPackage({
        ...premiumPackage,
        features: [...premiumPackage.features, premiumFeatureInput.trim()]
      });
      setPremiumFeatureInput('');
    }
  };

  const removeFeature = (packageType: 'basic' | 'standard' | 'premium', index: number) => {
    if (packageType === 'basic') {
      setBasicPackage({
        ...basicPackage,
        features: basicPackage.features.filter((_, i) => i !== index)
      });
    } else if (packageType === 'standard') {
      setStandardPackage({
        ...standardPackage,
        features: standardPackage.features.filter((_, i) => i !== index)
      });
    } else if (packageType === 'premium') {
      setPremiumPackage({
        ...premiumPackage,
        features: premiumPackage.features.filter((_, i) => i !== index)
      });
    }
  };

  // FAQ 관리
  const addFAQ = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const removeFAQ = (index: number) => {
    if (faqs.length > 1) {
      setFaqs(faqs.filter((_, i) => i !== index));
    }
  };

  const updateFAQ = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  // 폼 검증
  const validateForm = (): boolean => {
    if (!title.trim()) {
      setError(t('svCreate.errTitle'));
      return false;
    }
    if (!category) {
      setError(t('svCreate.errCategory'));
      return false;
    }
    if (!description.trim()) {
      setError(t('svCreate.errDesc'));
      return false;
    }
    if (basicPackage.price < 5000) {
      setError(t('svCreate.errMinPrice'));
      return false;
    }
    return true;
  };

  // 서비스 등록
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const serviceData = {
        title: title.trim(),
        category,
        description: description.trim(),
        package_basic: basicPackage,
        package_standard: standardPackage.price > 0 ? standardPackage : undefined,
        package_premium: premiumPackage.price > 0 ? premiumPackage : undefined,
        portfolio_images: portfolioImages,
        tags: tags.filter(t => t.trim()),
        faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        requirements: requirements.trim(),
        source_files_included: sourceFilesIncluded,
        commercial_use: commercialUse,
      };

      console.log('서비스 등록 요청:', serviceData);
      await serviceAPI.createService(serviceData);
      
      alert(t('svCreate.created'));
      navigate('/freelancer-services');
    } catch (err: any) {
      console.error('서비스 등록 실패:', err);
      setError(err.response?.data?.message || t('svCreate.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 7) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="service-create-page">
      <div className="container">
        <div className="page-header">
          <h1><Briefcase size={24} strokeWidth={2.2} /> {t('myServices.newService')}</h1>
          <p>{t('svCreate.subtitle')}</p>
        </div>

        {/* 진행 단계 표시 */}
        <div className="steps-wrapper">
          <div className="steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">{t('profile.basicInfo')}</div>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">{t('svCreate.stepPackage')}</div>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">{t('profile.portfolio')}</div>
            </div>
            <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
              <div className="step-number">4</div>
              <div className="step-label">{t('svCreate.stepTags')}</div>
            </div>
            <div className={`step ${currentStep >= 5 ? 'active' : ''}`}>
              <div className="step-number">5</div>
              <div className="step-label">FAQ</div>
            </div>
            <div className={`step ${currentStep >= 6 ? 'active' : ''}`}>
              <div className="step-number">6</div>
              <div className="step-label">{t('svCreate.stepReq')}</div>
            </div>
            <div className={`step ${currentStep >= 7 ? 'active' : ''}`}>
              <div className="step-number">7</div>
              <div className="step-label">{t('svCreate.stepOptions')}</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <div className="form-container">
          {/* 1단계: 기본 정보 */}
          {currentStep === 1 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">1</div>
                <h2>{t('svCreate.s1Title')}</h2>
              </div>

              <div className="form-group">
                <label>{t('svCreate.svcTitle')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('svCreate.titlePlaceholder')}
                  maxLength={100}
                />
                <small>{title.length}/100</small>
              </div>

              <div className="form-group">
                <label>{t('svCreate.categoryLabel')}</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">{t('svCreate.categorySelect')}</option>
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {categoryLabel(cat.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('svCreate.descLabel')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('svCreate.descPlaceholder')}
                  rows={8}
                  maxLength={2000}
                />
                <small>{description.length}/2000</small>
              </div>
            </div>
          )}

          {/* 2단계: 패키지 */}
          {currentStep === 2 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">2</div>
                <h2>{t('svCreate.s2Title')}</h2>
              </div>

              {/* 베이직 패키지 */}
              <div className="package-card">
                <h3><Gem size={17} /> {t('svCreate.basicPkg')}</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('quoteModal.priceLabel')}</label>
                    <input
                      type="number"
                      value={basicPackage.price}
                      onChange={(e) => setBasicPackage({ ...basicPackage, price: parseInt(e.target.value) || 0 })}
                      min="5000"
                      step="1000"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('svCreate.daysReq')}</label>
                    <input
                      type="number"
                      value={basicPackage.delivery_days}
                      onChange={(e) => setBasicPackage({ ...basicPackage, delivery_days: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('svCreate.revReq')}</label>
                    <input
                      type="number"
                      value={basicPackage.revisions}
                      onChange={(e) => setBasicPackage({ ...basicPackage, revisions: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('svCreate.features')}</label>
                  <div className="feature-input">
                    <input
                      type="text"
                      value={basicFeatureInput}
                      onChange={(e) => setBasicFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature('basic'))}
                      placeholder={t('svCreate.featurePlaceholder')}
                    />
                    <button type="button" onClick={() => addFeature('basic')}>{t('common.add')}</button>
                  </div>
                  <div className="feature-list">
                    {basicPackage.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span>✓ {feature}</span>
                        <button onClick={() => removeFeature('basic', index)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 스탠다드 패키지 */}
              <div className="package-card">
                <h3><Star size={17} /> {t('svCreate.stdPkg')}</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('svCreate.price')}</label>
                    <input
                      type="number"
                      value={standardPackage.price}
                      onChange={(e) => setStandardPackage({ ...standardPackage, price: parseInt(e.target.value) || 0 })}
                      min="0"
                      step="1000"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('svCreate.days')}</label>
                    <input
                      type="number"
                      value={standardPackage.delivery_days}
                      onChange={(e) => setStandardPackage({ ...standardPackage, delivery_days: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('quoteModal.revisionsLabel')}</label>
                    <input
                      type="number"
                      value={standardPackage.revisions}
                      onChange={(e) => setStandardPackage({ ...standardPackage, revisions: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('svCreate.features')}</label>
                  <div className="feature-input">
                    <input
                      type="text"
                      value={standardFeatureInput}
                      onChange={(e) => setStandardFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature('standard'))}
                      placeholder={t('svCreate.featurePlaceholder')}
                    />
                    <button type="button" onClick={() => addFeature('standard')}>{t('common.add')}</button>
                  </div>
                  <div className="feature-list">
                    {standardPackage.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span>✓ {feature}</span>
                        <button onClick={() => removeFeature('standard', index)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 프리미엄 패키지 */}
              <div className="package-card">
                <h3><Crown size={17} /> {t('svCreate.premPkg')}</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('svCreate.price')}</label>
                    <input
                      type="number"
                      value={premiumPackage.price}
                      onChange={(e) => setPremiumPackage({ ...premiumPackage, price: parseInt(e.target.value) || 0 })}
                      min="0"
                      step="1000"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('svCreate.days')}</label>
                    <input
                      type="number"
                      value={premiumPackage.delivery_days}
                      onChange={(e) => setPremiumPackage({ ...premiumPackage, delivery_days: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('quoteModal.revisionsLabel')}</label>
                    <input
                      type="number"
                      value={premiumPackage.revisions}
                      onChange={(e) => setPremiumPackage({ ...premiumPackage, revisions: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('svCreate.features')}</label>
                  <div className="feature-input">
                    <input
                      type="text"
                      value={premiumFeatureInput}
                      onChange={(e) => setPremiumFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature('premium'))}
                      placeholder={t('svCreate.featurePlaceholder')}
                    />
                    <button type="button" onClick={() => addFeature('premium')}>{t('common.add')}</button>
                  </div>
                  <div className="feature-list">
                    {premiumPackage.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span>✓ {feature}</span>
                        <button onClick={() => removeFeature('premium', index)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 3단계: 포트폴리오 */}
          {currentStep === 3 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">3</div>
                <h2>{t('svCreate.s3Title')}</h2>
              </div>

              <div className="form-group">
                <label>{t('svCreate.portfolioUrl')}</label>
                <div className="portfolio-input">
                  <input
                    type="text"
                    value={portfolioInput}
                    onChange={(e) => setPortfolioInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPortfolioImage())}
                    placeholder={t('svCreate.portfolioPlaceholder')}
                  />
                  <button type="button" onClick={addPortfolioImage}>{t('common.add')}</button>
                </div>
                <small>{t('svCreate.portfolioHint')}</small>
                <div className="portfolio-list">
                  {portfolioImages.map((url, index) => (
                    <div key={index} className="portfolio-item">
                      <img src={url} alt={`Portfolio ${index + 1}`} onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150'; }} />
                      <button onClick={() => removePortfolioImage(index)}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 4단계: 태그 */}
          {currentStep === 4 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">4</div>
                <h2>{t('svCreate.s4Title')}</h2>
              </div>

              <div className="form-group">
                <label>{t('svCreate.tagsLabel')}</label>
                <div className="tag-input">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder={t('svCreate.tagPlaceholder')}
                    disabled={tags.length >= 10}
                  />
                  <button type="button" onClick={addTag} disabled={tags.length >= 10}>
                    {t('common.add')}
                  </button>
                </div>
                <small>{t('svCreate.tagHint')}</small>
                <div className="tag-list">
                  {tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                      <button onClick={() => removeTag(index)}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 5단계: FAQ */}
          {currentStep === 5 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">5</div>
                <h2>{t('svCreate.s5Title')}</h2>
              </div>

              <div className="faq-section">
                {faqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <div className="faq-header">
                      <h4>FAQ {index + 1}</h4>
                      {faqs.length > 1 && (
                        <button type="button" onClick={() => removeFAQ(index)} className="btn-remove">
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                    <div className="form-group">
                      <label>{t('svCreate.question')}</label>
                      <input
                        type="text"
                        value={faq.question}
                        onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                        placeholder={t('svCreate.qPlaceholder')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('svCreate.answer')}</label>
                      <textarea
                        value={faq.answer}
                        onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                        placeholder={t('svCreate.aPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addFAQ} className="btn-add-faq">
                  + {t('svCreate.addFaq')}
                </button>
              </div>
            </div>
          )}

          {/* 6단계: 요구사항 */}
          {currentStep === 6 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">6</div>
                <h2>{t('svCreate.s6Title')}</h2>
              </div>

              <div className="form-group">
                <label>{t('svCreate.reqLabel')}</label>
                <textarea
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                  placeholder={t('svCreate.reqPlaceholder')}
                  rows={8}
                />
                <small>{t('svCreate.reqHint')}</small>
              </div>
            </div>
          )}

          {/* 7단계: 추가 옵션 */}
          {currentStep === 7 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-number">7</div>
                <h2>{t('svCreate.s7Title')}</h2>
              </div>

              <div className="options-section">
                <div className="option-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={sourceFilesIncluded}
                      onChange={(e) => setSourceFilesIncluded(e.target.checked)}
                    />
                    <span className="checkbox-text">
                      <strong>{t('svCreate.sourceFiles')}</strong>
                      <small>{t('svCreate.sourceFilesDesc')}</small>
                    </span>
                  </label>
                </div>

                <div className="option-item">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={commercialUse}
                      onChange={(e) => setCommercialUse(e.target.checked)}
                    />
                    <span className="checkbox-text">
                      <strong>{t('svCreate.commercial')}</strong>
                      <small>{t('svCreate.commercialDesc')}</small>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* 네비게이션 버튼 */}
          <div className="form-navigation">
            {currentStep > 1 && (
              <button className="btn-prev" onClick={prevStep}>
                ← {t('common.prev')}
              </button>
            )}
            {currentStep < 7 ? (
              <button 
                className="btn-next" 
                onClick={nextStep}
                disabled={
                  (currentStep === 1 && (!title || !category || !description)) ||
                  (currentStep === 2 && basicPackage.price < 5000)
                }
              >
                {t('common.next')} →
              </button>
            ) : (
              <button 
                className="btn-submit" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? t('svCreate.submitting') : t('dashboard.createService')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCreatePage;
